import { useState, useMemo, useRef, useEffect } from "react";
import { Grid, Button, Typography, Paper, Box } from "@mui/material";
import { toast } from "react-toastify";
import SurveyService from "../Services/SurveyService";
import { useTranslation } from "react-i18next";
import QuestionModel from "../Models/Survey/Questions/QuestionModel";
import PossibleAnswerModel from "../Models/Survey/Questions/PossibleAnswerModel";

export default function DragDropAnswer(props: {
  questionModel: QuestionModel;
  surveyId: string;
  setQuestionModel: Function;
  setIsFirstQuestion: Function;
  setCurrentQuestionId: Function;
  setLoading: Function;
}) {
  const { t, i18n } = useTranslation("survey");

  const surveyService = useMemo(() => new SurveyService(), []);
  const [items, setItems] = useState<PossibleAnswerModel[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragPreviewRef = useRef<HTMLDivElement | null>(null);
  const touchStartYRef = useRef<number | null>(null);

  useEffect(() => {
    //laad hier de vragen en zet ze in de juiste volgorde, indien back knop gebruikt en de vraag
    // is reed ingevuld dan zit de reeds opgeslagen volgorde in loadedSequence, indien niewe vraag dan gebruik je sequence (default volgorde)
    let sortedAnswers =
      props.questionModel.possibleAnswers?.sort(
        (a, b) =>
          (a.loadedSequence ?? a.sequence) - (b.loadedSequence ?? b.sequence),
      ) ?? [];

    setItems(sortedAnswers);
  }, [props.questionModel]);

  const createPreview = (text: string) => {
    const preview = document.createElement("div");
    preview.style.position = "absolute";
    preview.style.top = "-1000px";
    preview.style.left = "-1000px";
    preview.style.padding = "10px";
    preview.style.width = "280px";
    preview.style.background = "#1876d5";
    preview.style.color = "#ffffff";
    preview.style.border = "2px solid #1976d2";
    preview.style.borderRadius = "12px";
    preview.style.boxShadow = "0 12px 30px rgba(0,0,0,0.3)";
    preview.style.fontFamily = "Roboto, sans-serif";
    preview.style.fontSize = "14px";
    preview.style.fontWeight = "500";
    preview.innerText = text;
    document.body.appendChild(preview);
    dragPreviewRef.current = preview;
  };

  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => {
    setDraggedIndex(index);
    createPreview(items[index].answer);
    e.dataTransfer.setDragImage(dragPreviewRef.current!, 140, 20);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
    if (dragPreviewRef.current) {
      document.body.removeChild(dragPreviewRef.current);
      dragPreviewRef.current = null;
    }
  };

  const handleDragOver = (
    e: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = (index: number) => {
    if (draggedIndex === null) return;

    const updated = [...items];
    const [removed] = updated.splice(draggedIndex, 1);
    updated.splice(index, 0, removed);

    //update ook de loadedSequence van alle items zodat ze verplaatsen
    const reordered = updated.map((item, idx) => ({
      ...item,
      loadedSequence: idx + 1, // or idx if you want 0-based
    }));

    setItems(reordered);
    handleDragEnd();
  };

  // voor mobile, zelfde maar dan voor touch events
  const handleTouchStart = (
    e: React.TouchEvent<HTMLDivElement>,
    index: number,
  ) => {
    setDraggedIndex(index);
    createPreview(items[index].answer);
    touchStartYRef.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!draggedIndex && draggedIndex !== 0) return;
    const touchY = e.touches[0].clientY;

    const deltaY = touchY - (touchStartYRef.current ?? touchY);
    const currentIndex = draggedIndex!;
    let targetIndex = currentIndex;

    if (deltaY > 30 && currentIndex < items.length - 1) {
      targetIndex = currentIndex + 1;
    } else if (deltaY < -30 && currentIndex > 0) {
      targetIndex = currentIndex - 1;
    }

    if (targetIndex !== currentIndex) {
      const updated = [...items];
      const [removed] = updated.splice(currentIndex, 1);
      updated.splice(targetIndex, 0, removed);

      const reordered = updated.map((item, idx) => ({
        ...item,
        loadedSequence: idx + 1,
      }));

      setItems(reordered);
      setDraggedIndex(targetIndex);
      touchStartYRef.current = touchY;
    }
  };

  const handleTouchEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
    if (dragPreviewRef.current) {
      document.body.removeChild(dragPreviewRef.current);
      dragPreviewRef.current = null;
    }
  };

  if (props.questionModel !== undefined) {
    return (
      <>
        <Box sx={{ width: 300, margin: "auto", mt: 4 }}>
          {items.map((item, index) => {
            const isDragging = draggedIndex === index;
            const isDragOver = dragOverIndex === index;

            return (
              <Paper
                key={item.id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={() => handleDrop(index)}
                onTouchStart={(e) => handleTouchStart(e, index)}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                className="dragDropBox"
                elevation={isDragging ? 12 : isDragOver ? 6 : 1}
                sx={{
                  p: 1.5,
                  mb: 1,
                  cursor: isDragging ? "grabbing" : "grab",
                  transition: "all 0.2s ease",
                  transform: isDragging ? "scale(1.08)" : "scale(1)",
                  border: isDragOver
                    ? "2px dashed #1976d2"
                    : "1px solid transparent",
                  backgroundColor: isDragOver
                    ? "rgba(25, 118, 210, 0.15)"
                    : "#e3f2fd",
                  boxShadow: isDragging
                    ? "0 10px 25px rgba(0,0,0,0.25)"
                    : undefined,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: isDragging ? "rgba(13, 71, 161, 0.5)" : "#ffffff",
                    userSelect: "none",
                  }}
                >
                  {t(item.answer)}
                </Typography>
              </Paper>
            );
          })}
        </Box>

        <Grid justifyContent="center" container p={3}>
          <Button
            className="float-start"
            variant="contained"
            key={props.questionModel.id}
            style={{
              textTransform: "none",
              maxWidth: "100%",
            }}
            onClick={() => clickAnswer()}
          >
            {t("Volgende vraag")}
          </Button>
        </Grid>

        {props.questionModel.imageBase64String && (
          <img
            src={`data:${props.questionModel.imageType};base64,${props.questionModel.imageBase64String}`}
            alt="Vraag afbeelding"
            style={{
              marginTop: "1rem",
              maxWidth: "100%",
              maxHeight: "350px",
              height: "auto",
              display: "block",
            }}
          />
        )}
      </>
    );
  }

  return <></>;

  function clickAnswer() {
    if (props.questionModel) {
      props.setLoading(true);
      surveyService
        .saveAnswer(
          {
            surveyId: props.surveyId,
            questionId: props.questionModel.id,
            answer: items?.map((item) => item.sequence).join(", ") ?? "",
            nextQuestionId: null,
            possibleAnswerId: null,
          },
          i18n.language,
        )
        .then((result) => {
          if (result) {
            if (result.success) {
              //do stuff with question
              if (result.nextQuestionModel !== null) {
                //set Progress bar
                props.setQuestionModel(result.nextQuestionModel);
                props.setIsFirstQuestion(
                  result.nextQuestionModel.thisIsTheFirstQuestion,
                );
                props.setCurrentQuestionId(result.nextQuestionModel.id);
              }
            } else {
              toast.error(t("Fout bij opslaan van vraag."));
            }
          }
          props.setLoading(false);
        });
    }
  }
}
